import React from 'react';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import CalendarDay from './CalendarDay';
import { getEventGridLabel } from './calendarEventTypes';

/**
 * Grilla del calendario
 * 
 * Muestra días según el modo de vista:
 * - month: Grid completo del mes 
 * - week: Solo los 7 días de la semana seleccionada
 * - day: Un solo día en vista detallada
 * 
 * @param {string} viewMode - Modo de vista: 'month', 'week', 'day'
 * @param {moment.Moment} selectedDate - Fecha seleccionada
 * @param {Object} eventsByDay - Objeto con eventos agrupados por día (YYYY-MM-DD)
 * @param {moment.Moment} selectedDay - Día seleccionado para resaltar
 * @param {Function} onDayClick - Callback cuando se hace click en un día
 * @param {boolean} loading - true mientras se cargan los eventos del mes
 */
function CalendarGrid({ viewMode, selectedDate, eventsByDay, selectedDay, onDayClick, loading }) {
	moment.locale('es');

	let days = [];
	let startDate, endDate;

	if (viewMode === 'month') {
		// Vista mensual
		const monthStart = moment.utc(selectedDate).startOf('month');
		const monthEnd = moment.utc(selectedDate).endOf('month');
		startDate = monthStart.clone().startOf('week');
		endDate = monthEnd.clone().endOf('week');
	} else if (viewMode === 'week') {
		// Vista semanal
		startDate = moment.utc(selectedDate).startOf('week');
		endDate = moment.utc(selectedDate).endOf('week');
	} else if (viewMode === 'day') {
		// Vista diaria
		startDate = moment.utc(selectedDate).startOf('day');
		endDate = moment.utc(selectedDate).endOf('day');
	}

	const currentDate = startDate.clone();
	while (currentDate.isSameOrBefore(endDate, 'day')) {
		days.push(currentDate.clone());
		currentDate.add(1, 'day');
	}

	// Días de la semana
	const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

	// Para vista diaria, mostrar solo el día seleccionado
	if (viewMode === 'day') {
		return (
			<div className='calendar-grid calendar-day-view'>
				<div className='calendar-day-detailed'>
					{days.map((day, index) => {
						const dayKey = day.format('YYYY-MM-DD');
						const dayEvents = eventsByDay[dayKey] || [];
						const isToday = day.isSame(moment.utc(), 'day');

						return (
							<div key={index} className='calendar-day-detailed-container'>
								<div className='calendar-day-detailed-header'>
									<h3 className='calendar-day-detailed-title'>
										{day.format('dddd, D [de] MMMM [de] YYYY')}
									</h3>
									{isToday && (
										<span className='badge bg-primary ms-2'>Hoy</span>
									)}
								</div>
								<div className='calendar-day-detailed-events'>
									{loading ? (
										// viewMode 'day' is unreachable from Dashboard today (the Vista
										// toggle was removed — see CalendarView's viewMode comment), so
										// this is a minimal placeholder for when it's re-enabled, not a
										// polished skeleton.
										Array.from({ length: 3 }, (_, i) => (
											<div key={i} className='calendar-day-detailed-event' style={{ borderLeftColor: 'transparent' }}>
												<span aria-hidden='true' className='el-sk tw:block tw:h-[13px] tw:w-2/3' />
											</div>
										))
									) : dayEvents.length > 0 ? (
										dayEvents.map((event, eventIndex) => (
											<div
												key={eventIndex}
												className='calendar-day-detailed-event'
												style={{ borderLeftColor: event.color || '#6c757d' }}
												onClick={() => onDayClick(day, [event])}
											>
												<div className='calendar-day-detailed-event-header'>
													<span
														className='calendar-day-detailed-event-type'
														style={{ backgroundColor: event.color || '#6c757d' }}
													>
														{getEventGridLabel(event.type)}
													</span>
													<span className='calendar-day-detailed-event-time'>
														{moment.utc(event.date).format('HH:mm')}
													</span>
												</div>
												<div className='calendar-day-detailed-event-title'>
													{event.title}
												</div>
												{event.description && (
													<div className='calendar-day-detailed-event-description'>
														{event.description}
													</div>
												)}
												{event.metadata && (
													<div className='calendar-day-detailed-event-metadata'>
														{event.metadata.amount && (
															<div className='calendar-day-detailed-event-amount'>
																Monto: {event.metadata.currency || ''} {event.metadata.amount}
															</div>
														)}
														{event.metadata.ownerName && (
															<div className='calendar-day-detailed-event-owner'>
																{event.metadata.ownerName}
															</div>
														)}
														{event.metadata.propertyName && (
															<div className='calendar-day-detailed-event-property'>
																Propiedad: {event.metadata.propertyName}
															</div>
														)}
													</div>
												)}
											</div>
										))
									) : (
										<div className='calendar-day-detailed-empty'>
											No hay eventos para este día
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	// Para vista semanal, mostrar horas del día (0-23) y días en columnas
	if (viewMode === 'week') {
		const hours = Array.from({ length: 24 }, (_, i) => i); // 0-23
		
		return (
			<div className='calendar-grid calendar-week-view calendar-week-hours-view'>
				<div className='calendar-week-hours-container'>
					<div className='calendar-hours-column'>
						<div className='calendar-hour-label-header'></div>
						{hours.map(hour => (
							<div key={hour} className='calendar-hour-label'>
								{hour.toString().padStart(2, '0')}:00
							</div>
						))}
					</div>
					<div className='calendar-week-days-container'>
						<Row className='calendar-weekdays mb-2'>
							{days.map((day, index) => (
								<Col key={index} className='text-center fw-bold calendar-weekday-header'>
									<div>{weekDays[day.day()]}</div>
									<div className='calendar-weekday-date'>{day.format('D/M')}</div>
								</Col>
							))}
						</Row>
						<div className='calendar-week-hours-grid'>
							{days.map((day, dayIndex) => {
								const dayKey = day.format('YYYY-MM-DD');
								const dayEvents = eventsByDay[dayKey] || [];
								
								return (
									<div key={dayIndex} className='calendar-week-day-column'>
										{hours.map(hour => {
											// Filtrar eventos que corresponden a esta hora
											const hourEvents = dayEvents.filter(event => {
												const eventDate = moment.utc(event.date);
												return eventDate.hour() === hour;
											});
											
											return (
												<div
													key={hour}
													className='calendar-week-hour-cell'
													onClick={() => onDayClick(day, dayEvents)}
												>
													{hourEvents.slice(0, 2).map((event, eventIndex) => (
														<div
															key={eventIndex}
															className='calendar-week-event'
															style={{ backgroundColor: event.color || '#6c757d' }}
															title={event.title}
															onClick={(e) => {
																e.stopPropagation();
																onDayClick(day, [event]);
															}}
														>
															{event.title.length > 25 ? event.title.substring(0, 25) + '...' : event.title}
														</div>
													))}
													{hourEvents.length > 2 && (
														<div
															className='calendar-week-event-more'
															title={`${hourEvents.length - 2} eventos más`}
														>
															+{hourEvents.length - 2}
														</div>
													)}
												</div>
											);
										})}
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Vista mensual (default)
	return (
		<div className='calendar-grid calendar-month-view'>
			{/* Header con días de la semana */}
			<Row className='calendar-weekdays mb-2'>
				{weekDays.map((day, index) => (
					<Col key={index} className='text-center fw-bold'>
						{day}
					</Col>
				))}
			</Row>

			{/* Grid de días */}
			<div className='calendar-days'>
				{days.map((day, index) => {
					const dayKey = day.format('YYYY-MM-DD');
					const dayEvents = eventsByDay[dayKey] || [];
					const isCurrentMonth = day.isSame(selectedDate, 'month');
					const isToday = day.isSame(moment.utc(), 'day');
					const isSelected = selectedDay && day.isSame(moment.utc(selectedDay), 'day');

					return (
						<CalendarDay
							key={index}
							day={day}
							events={dayEvents}
							isCurrentMonth={isCurrentMonth}
							isToday={isToday}
							isSelected={isSelected}
							onClick={() => onDayClick(day, dayEvents)}
							loading={loading}
						/>
					);
				})}
			</div>
		</div>
	);
}

export default CalendarGrid;

